import { ReloadOutlined } from '@ant-design/icons';
import { EAdminAuthEvent, type IAdminAuthLogEntry } from '@mahawi/eshop-common';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { authLogLoad } from '../reducers/user/reducer';
import { SPACING } from '../theme/tokens';
import TableComponent from './table';

const { Title, Text } = Typography;

const EVENT_TAGS: Record<EAdminAuthEvent, { label: string; color: string }> = {
  [EAdminAuthEvent.LOGIN_PASSWORD_REJECTED]: {
    label: 'Wrong password',
    color: 'red',
  },
  [EAdminAuthEvent.LOGIN_COUNTRY_BLOCKED]: {
    label: 'Login blocked (country)',
    color: 'volcano',
  },
  [EAdminAuthEvent.LOGIN_RATE_LIMITED]: {
    label: 'Login rate limited',
    color: 'volcano',
  },
  [EAdminAuthEvent.MAGIC_LINK_SENT]: {
    label: 'Magic link sent',
    color: 'blue',
  },
  [EAdminAuthEvent.MAGIC_LINK_CONSUMED]: {
    label: 'Signed in',
    color: 'green',
  },
  [EAdminAuthEvent.MAGIC_LINK_INVALID]: {
    label: 'Invalid magic link',
    color: 'orange',
  },
  [EAdminAuthEvent.MAGIC_LINK_COUNTRY_BLOCKED]: {
    label: 'Magic link blocked (country)',
    color: 'volcano',
  },
  [EAdminAuthEvent.LOGOUT]: { label: 'Logout', color: 'default' },
  [EAdminAuthEvent.PASSWORD_CHANGED]: {
    label: 'Password changed',
    color: 'purple',
  },
  [EAdminAuthEvent.PASSWORD_CHANGE_REJECTED]: {
    label: 'Password change rejected',
    color: 'orange',
  },
  [EAdminAuthEvent.SESSION_REVOKED]: {
    label: 'Session revoked',
    color: 'magenta',
  },
  [EAdminAuthEvent.OTHER_SESSIONS_REVOKED]: {
    label: 'Other sessions revoked',
    color: 'magenta',
  },
  [EAdminAuthEvent.SESSION_COUNTRY_BLOCKED]: {
    label: 'Session revoked (country)',
    color: 'volcano',
  },
  [EAdminAuthEvent.NEW_SIGN_IN_NOTIFIED]: {
    label: 'New sign-in alert sent',
    color: 'gold',
  },
};

type TAuthLogRow = IAdminAuthLogEntry & { key: string; identity: string };

const columns: ColumnsType<TAuthLogRow> = [
  {
    title: 'Time',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 170,
    defaultSortOrder: 'descend',
    sorter: (a: TAuthLogRow, b: TAuthLogRow): number =>
      dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    render: (createdAt: string): string =>
      dayjs(createdAt).format(EDateFormat.DATE_TIME),
  },
  {
    title: 'Event',
    dataIndex: 'event',
    key: 'event',
    filters: Object.values(EAdminAuthEvent).map((event: EAdminAuthEvent) => ({
      text: EVENT_TAGS[event].label,
      value: event,
    })),
    onFilter: (value, record: TAuthLogRow): boolean => record.event === value,
    render: (event: EAdminAuthEvent): React.ReactElement => (
      <Tag color={EVENT_TAGS[event].color}>{EVENT_TAGS[event].label}</Tag>
    ),
  },
  {
    title: 'Account',
    dataIndex: 'identity',
    key: 'identity',
    sorter: (a: TAuthLogRow, b: TAuthLogRow): number =>
      stringCompare(a.identity, b.identity),
    ...getColumnSearch<TAuthLogRow>('identity'),
  },
  {
    title: 'IP',
    dataIndex: 'ip',
    key: 'ip',
    ...getColumnSearch<TAuthLogRow>('ip'),
    render: (ip: string | null): React.ReactNode =>
      ip ?? <Text type="secondary">{EMPTY_VALUE}</Text>,
  },
  {
    title: 'Country',
    dataIndex: 'country',
    key: 'country',
    width: 100,
    sorter: (a: TAuthLogRow, b: TAuthLogRow): number =>
      stringCompare(a.country ?? '', b.country ?? ''),
    ...getColumnSearch<TAuthLogRow>('country'),
    render: (country: string | null): React.ReactNode =>
      country ?? <Text type="secondary">{EMPTY_VALUE}</Text>,
  },
  {
    title: 'Browser',
    dataIndex: 'userAgent',
    key: 'userAgent',
    ellipsis: { showTitle: false },
    render: (userAgent: string | null): React.ReactNode =>
      userAgent ? (
        <Tooltip title={userAgent}>{userAgent}</Tooltip>
      ) : (
        <Text type="secondary">{EMPTY_VALUE}</Text>
      ),
  },
  {
    title: 'Cloudflare Ray',
    dataIndex: 'cfRay',
    key: 'cfRay',
    render: (cfRay: string | null): React.ReactNode =>
      cfRay ? (
        <Text copyable>{cfRay}</Text>
      ) : (
        <Text type="secondary">{EMPTY_VALUE}</Text>
      ),
  },
];

function AdminAuthLogTable(): React.ReactElement {
  const dispatch = useAppDispatch();
  const authLog = useSelector((s: RootState) => s.User.authLog);
  const authLogInProcess = useSelector(
    (s: RootState) => s.User.authLogInProcess,
  );

  useEffect(() => {
    dispatch(authLogLoad());
  }, [dispatch]);

  return (
    <>
      <Space size={SPACING.lg} align="start">
        <Title level={4}>Security log</Title>
        <Tooltip title="Reload">
          <Button
            onClick={(): void => {
              dispatch(authLogLoad());
            }}
          >
            <ReloadOutlined />
          </Button>
        </Tooltip>
      </Space>

      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={authLog.map((entry: IAdminAuthLogEntry): TAuthLogRow => ({
          ...entry,
          key: entry.uuid,
          identity: entry.account?.email ?? entry.email ?? '',
        }))}
        isLoading={authLogInProcess}
      />
    </>
  );
}

export default AdminAuthLogTable;
